import React, { useCallback, useContext, useMemo, useState } from 'react';
import _get from 'lodash/get';
import _classNames from 'classnames';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Hidden from '@material-ui/core/Hidden';
import useModalFocusTrap from 'components/Hooks/useModalFocusTrap';
import { useSelector } from 'react-redux';
import { getAllSeries } from 'containers/Series/selectors';
import { getAllCourses } from 'containers/Course/selectors';
import { getAllPractices } from 'containers/Practices/selectors';
import CourseItem from 'components/CourseItem';
import SeriesItem from 'components/SeriesItem';
import PracticeItem from 'components/PracticeItem';
import { checkIfActiveVariant } from 'utils/abTestsUtils';
import {
  getFeaturedItem,
  COURSES_TYPE,
  PRACTICES_TYPE,
  SERIES_TYPE,
} from './utils';
import LearningLabItem from './LearningLabItem';
import NavBarContext from '../../../navBarContext';

const useStyles = makeStyles(theme => ({
  root: {
    padding: 16,
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
    [theme.breakpoints.up('600')]: {
      display: 'flex',
    },
  },
  rootBVariant: {
    padding: '30px 30px 30px 0',
    gap: 24,
  },
  leftContent: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 400,
    width: '100%',
  },
  leftContentBVariant: {
    borderRight: '1px solid #E0E0E0',
  },
  rightContent: {
    display: 'flex',
    flexDirection: 'column',
    width: 215,
  },
  header: {
    fontSize: 16,
    lineHeight: '22px',
    marginBottom: 16,
  },
}));

const parseFeaturedItemContent = (
  featuredItem,
  featuredItemType,
  clickHandler,
) => {
  if (featuredItemType === SERIES_TYPE) {
    return (
      <SeriesItem
        item={{ ...featuredItem.fields, id: featuredItem.sys.id }}
        shouldWhiteBackgroundOnCompleted
        onClick={() => clickHandler(featuredItem.fields.name, 'featured card')}
        trackingProps={{
          section: 'menu',
        }}
      />
    );
  }
  if (featuredItemType === COURSES_TYPE) {
    return (
      <CourseItem
        item={{ ...featuredItem.fields, id: featuredItem.sys.id }}
        onClick={() => clickHandler(featuredItem.fields.name, 'featured card')}
        trackingProps={{
          section: 'menu',
        }}
      />
    );
  }
  if (featuredItemType === PRACTICES_TYPE) {
    return (
      <PracticeItem
        item={{ ...featuredItem.fields, id: featuredItem.sys.id }}
        onClick={() => clickHandler(featuredItem.fields.name, 'featured card')}
        trackingProps={{
          section: 'menu',
        }}
      />
    );
  }

  return null;
};

function LearningLab({ onClose, onFocusNext }) {
  const { siteCopy, sendMixpanelEvent } = useContext(NavBarContext);
  const { refCallback } = useModalFocusTrap(onClose, onFocusNext);
  const isBVariant = checkIfActiveVariant(
    'learning-lab-popover',
    'B-WithFeatured',
  );

  const [hoveredType, setHoveredType] = useState();
  const courses = useSelector(getAllCourses);
  const practices = useSelector(getAllPractices);
  const series = useSelector(getAllSeries);

  const classes = useStyles();

  const clickHandler = useCallback(
    (name, element) => {
      sendMixpanelEvent('Menu Item - Clicked', {
        type: 'Learning Lab',
        name,
        element,
      });
    },
    [sendMixpanelEvent],
  );

  const [featuredItem, featuredItemType] = useMemo(
    () => getFeaturedItem(hoveredType, series, courses, practices),
    [hoveredType, series, courses, practices],
  );

  const hasFeatured = !!featuredItem;

  return (
    <div
      className={_classNames(classes.root, {
        [classes.rootBVariant]: isBVariant,
      })}
      ref={refCallback}
      role="menu"
      aria-label="Learning Lab Menu"
    >
      <div
        className={_classNames(classes.leftContent, {
          [classes.leftContentBVariant]: isBVariant,
        })}
      >
        {series.length > 0 && (
          <LearningLabItem
            type="series"
            iconType="Series"
            onClick={() => clickHandler('Series', 'text')}
            onHover={() => setHoveredType(SERIES_TYPE)}
          />
        )}
        {courses.length > 0 && (
          <LearningLabItem
            type="courses"
            iconType="Courses"
            onClick={() => clickHandler('Courses', 'text')}
            onHover={() => setHoveredType(COURSES_TYPE)}
          />
        )}
        {practices.length > 0 && (
          <LearningLabItem
            type="practices"
            iconType="Practices"
            onClick={() => clickHandler('Practices', 'text')}
            onHover={() => setHoveredType(PRACTICES_TYPE)}
          />
        )}
      </div>
      {hasFeatured && (
        <div className={classes.rightContent}>
          <div data-test-id="menu-learning-lab-header">
            <Typography
              variant="h6"
              color="textPrimary"
              className={classes.header}
            >
              {_get(siteCopy, [
                'pageCopy',
                'submenu-learning-lab',
                'featured-label',
              ])}
            </Typography>
          </div>
          <Hidden xsDown>
            <div data-test-id="learning-lab-featured">
              {parseFeaturedItemContent(
                featuredItem,
                featuredItemType,
                clickHandler,
              )}
            </div>
          </Hidden>
        </div>
      )}
    </div>
  );
}

export default LearningLab;
